import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { X, Plus, Trash2, TableIcon, MousePointerSquareDashed, TableCellsMerge, TableCellsSplit } from 'lucide-react'
import { toast } from 'sonner'
import { useAppStore } from '@/store'
import { useTranslation } from '@/i18n'
import { Button } from '@/components/ui/button'
import type { TableCellNode } from '@/service/graph'
import {
  groupCellsIntoTables,
  buildBlankTable,
  nextTableIndex,
  cellKey,
  cellText,
  coveredBySpan,
  insertRow,
  insertColumn,
  deleteRow,
  deleteColumn,
  deleteTable,
  mergeCells,
  unmergeCells,
  findCellById,
  reclassifyHeaders,
  DEFAULT_HEADER_TYPES,
  MAX_HEADER_ROWS,
  HEADER_TYPE_PROPERTY_NAME,
  HEADER_TYPE_STREAM_NAME,
  HEADER_TYPE_STREAM_NUMBER,
  type HeaderTypes,
  type TableModel,
  type TableMutation,
  type TableRegion,
} from './tableGrid'
import { detectTableFromText, buildTableFromDetection } from './tableOcrGrid'

interface TableGridPanelV2Props {
  isOpen: boolean
  onClose: () => void
  /** Start the draw-a-table-region flow for a table that was never parsed. */
  onCreateTable?: () => void
  /**
   * True while the canvas waits for the region drag. The panel has to know: a CSS cursor
   * belongs to the element under the pointer, so the canvas crosshair says nothing while the
   * pointer is still on the button — without this, the click looks like it did nothing.
   */
  isDrawingRegion?: boolean
  /** The region the annotator just drew on the canvas, awaiting a row/column count. */
  pendingRegion?: TableRegion | null
  /** Called once that region has been turned into a blank grid (or abandoned). */
  onPendingRegionConsumed?: () => void
  /** The canvas multi-selection of table cells — what merge/unmerge act on. */
  selectedCellIds?: string[]
  /** Reports the focused cell so the canvas can spotlight it (side-by-side compare). */
  onFocusCellChange?: (nodeId: string | null) => void
  /** Reports which table is on screen, so the canvas shows only that table's cells. */
  onActiveTableChange?: (tableIndex: number | null) => void
  /** A cell clicked on the canvas — scroll it into view and focus its input. */
  focusCellId?: string | null
}

const TOP_ROW_TYPES = [HEADER_TYPE_STREAM_NUMBER, HEADER_TYPE_STREAM_NAME]
const LEFT_COL_TYPES = [HEADER_TYPE_PROPERTY_NAME, HEADER_TYPE_STREAM_NAME]

/** Grow/shrink the header-row list, keeping the chosen types. A new row defaults to the type
 *  the band lacks — stacked headers are the name/number pair far more often than a repeat. */
const withHeaderRowCount = (current: HeaderTypes, count: number): HeaderTypes => {
  const n = Math.max(1, Math.min(MAX_HEADER_ROWS, count))
  const rowTypes = current.rowTypes.slice(0, n)
  while (rowTypes.length < n) {
    rowTypes.push(
      rowTypes.includes(HEADER_TYPE_STREAM_NAME) ? HEADER_TYPE_STREAM_NUMBER : HEADER_TYPE_STREAM_NAME,
    )
  }
  return { ...current, rowTypes }
}

/**
 * Editable grid for the material balance tables on a PFD. Pivots the table's `table_cell`
 * nodes back into their row/column layout, so the annotator reads down a column against the
 * drawing instead of boxing hundreds of cells one at a time.
 *
 * Three ways in, all producing the same node type — so save, undo, the canvas and the export
 * need no special case: cells the parser already emitted, cells read from the drawing's OCR,
 * or a blank grid to type into.
 */
export default function TableGridPanelV2({
  isOpen,
  onClose,
  onCreateTable,
  isDrawingRegion = false,
  pendingRegion,
  onPendingRegionConsumed,
  onFocusCellChange,
  onActiveTableChange,
  focusCellId,
  selectedCellIds = [],
}: TableGridPanelV2Props) {
  const { t } = useTranslation()
  const { currentState, updateNode, addNodes, updateMultipleNodes, deleteMultipleNodes, recordHistory } = useAppStore()

  const [selectedTableIndex, setSelectedTableIndex] = useState<number | null>(null)
  // Keyed by table: the bands describe the drawing's table, so carrying one table's layout
  // onto another mis-classifies its cells and mis-shades its grid.
  const [headerTypesByTable, setHeaderTypesByTable] = useState<Record<number, HeaderTypes>>({})
  // Size for a from-scratch table.
  const [newRows, setNewRows] = useState('6')
  const [newCols, setNewCols] = useState('5')
  // Held locally so each keystroke doesn't hit the store; committed on blur.
  const [draft, setDraft] = useState<{ id: string; value: string } | null>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  const tables = useMemo(
    () => groupCellsIntoTables(
      // Narrow to table cells before pivoting so the models come back typed as store
      // nodes — the panel writes them back through `updateNode`.
      Array.from(currentState.nodes.values()).filter(
        (n): n is TableCellNode => n.type === 'table_cell',
      ),
    ),
    [currentState.nodes],
  )

  // Keep the selection valid as tables appear and disappear.
  useEffect(() => {
    if (tables.length === 0) {
      if (selectedTableIndex !== null) setSelectedTableIndex(null)
      return
    }
    if (selectedTableIndex === null || !tables.some((tb) => tb.tableIndex === selectedTableIndex)) {
      // The FIRST table on the drawing — `groupCellsIntoTables` sorts by index, so the last
      // entry is the furthest down the sheet, which is a surprising thing to open on.
      setSelectedTableIndex(tables[0].tableIndex)
    }
  }, [tables, selectedTableIndex])

  const table: TableModel | undefined = useMemo(
    () => tables.find((tb) => tb.tableIndex === selectedTableIndex),
    [tables, selectedTableIndex],
  )

  const headerTypes = (selectedTableIndex !== null && headerTypesByTable[selectedTableIndex])
    || DEFAULT_HEADER_TYPES

  // Which table the canvas should draw; none once the panel closes.
  useEffect(() => {
    onActiveTableChange?.(isOpen ? selectedTableIndex : null)
  }, [isOpen, selectedTableIndex, onActiveTableChange])

  // A cell clicked on the drawing focuses here. Also keyed on the selected table: the grid
  // renders empty for one frame on first open, so a cell picked then has nothing to focus.
  useEffect(() => {
    if (!focusCellId || !gridRef.current) return
    const input = gridRef.current.querySelector<HTMLInputElement>(`[data-cell-id="${focusCellId}"]`)
    input?.focus()
    input?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [focusCellId, selectedTableIndex])

  /** One undoable step: snapshot, then add / shift / delete. */
  const applyMutation = useCallback((mutation: TableMutation) => {
    if (!mutation.add.length && !mutation.shift.length && !mutation.deleteIds.length) return
    recordHistory()
    if (mutation.deleteIds.length) deleteMultipleNodes(mutation.deleteIds)
    for (const { ids, patch } of mutation.shift) updateMultipleNodes(ids, patch)
    if (mutation.add.length) addNodes(mutation.add)
  }, [recordHistory, deleteMultipleNodes, updateMultipleNodes, addNodes])

  /**
   * Merge/unmerge act on the CANVAS multi-selection: pick the Select tool, click each cell,
   * and the actions appear. Reported up from EditorWrapper through the route, since the
   * selection lives on the canvas side and this panel is its sibling.
   *
   * They APPEAR rather than sitting permanently disabled, because a selection is a deliberate
   * gesture — a disabled pair with no selection is just noise in a toolbar that already has
   * five buttons.
   */
  const selection = useMemo(
    () =>
      table
        ? selectedCellIds
            .map((id) => findCellById(table, id))
            .filter((f): f is NonNullable<typeof f> => f !== null)
        : [],
    [table, selectedCellIds],
  )
  // Mergeable = 2+ cells, one row, no gap once spans are accounted for. Mirrors mergeCells'
  // own refusal, so the button never appears for something it would decline to do.
  const canMerge = useMemo(() => {
    if (selection.length < 2) return false
    const row = selection[0].row
    if (selection.some((f) => f.row !== row)) return false
    const sorted = [...selection].sort((a, b) => a.col - b.col)
    return sorted.every((f, i) => i === 0 || f.col === sorted[i - 1].col + sorted[i - 1].span)
  }, [selection])
  // EVERY selected cell must be merged, not merely one of them. Clicking accumulates in
  // Select mode, so `some` kept offering Unmerge for a selection whose latest box was a plain
  // cell — it read as "this unmerged box says Unmerge". Requiring all of them means the action
  // only ever appears when splitting is unambiguously what the selection asks for.
  const canUnmerge = useMemo(
    () => selection.length > 0 && selection.every((f) => f.span > 1),
    [selection],
  )

  const commitCell = useCallback((cell: TableCellNode, value: string) => {
    setDraft(null)
    if (value === cellText(cell)) return
    // Snapshot first, or Undo skips past the edit and reverts an earlier action instead.
    recordHistory()
    // `name` mirrors `content`, as the parser writes them.
    updateNode({ ...cell, content: value, name: value })
  }, [recordHistory, updateNode])

  // What OCR already read inside the drawn region. Beats the alternatives: the values are
  // there to verify rather than type, and the boxes follow the table's real ruling.
  const detected = useMemo(
    () => (pendingRegion ? detectTableFromText(currentState.nodes.values(), pendingRegion) : null),
    [pendingRegion, currentState.nodes],
  )

  /** Materialize the detected table: real geometry, OCR text pre-filled. */
  const createTableFromText = useCallback(() => {
    if (!detected) return
    const tableIndex = nextTableIndex(tables)
    recordHistory()
    // Purely ADDITIVE — the OCR text nodes it read are left alone. Deleting them made the
    // conversion destructive: removing the table afterwards then lost the detections too,
    // with no way back. Double-counting is prevented at export instead, where a tag inside
    // an exported table's box is omitted from `tags[]`.
    addNodes(buildTableFromDetection(tableIndex, detected, headerTypes))
    setSelectedTableIndex(tableIndex)
    onPendingRegionConsumed?.()
    toast.success(t('graph_editor:tableGrid.detectedTableCreated', {
      rows: detected.nRows, cols: detected.nCols, values: detected.filledCells,
      defaultValue: 'Table read from the drawing — {{rows}}×{{cols}}, {{values}} values filled in',
    }))
  }, [detected, tables, headerTypes, recordHistory, addNodes, onPendingRegionConsumed, t])

  /** Blank grid from the drawn region — the only path that interpolates a whole table. */
  const createBlankTable = useCallback(() => {
    if (!pendingRegion) return
    const rows = Math.max(1, Math.min(200, Number.parseInt(newRows, 10) || 0))
    const cols = Math.max(1, Math.min(100, Number.parseInt(newCols, 10) || 0))
    const tableIndex = nextTableIndex(tables)
    recordHistory()
    addNodes(buildBlankTable(tableIndex, pendingRegion, rows, cols, headerTypes))
    setSelectedTableIndex(tableIndex)
    onPendingRegionConsumed?.()
    toast.success(t('graph_editor:tableGrid.newTableCreated', {
      rows, cols, defaultValue: 'Blank table added — {{rows}}×{{cols}}',
    }))
  }, [pendingRegion, newRows, newCols, tables, headerTypes, recordHistory, addNodes, onPendingRegionConsumed, t])

  const changeHeaderTypes = useCallback((next: HeaderTypes) => {
    if (selectedTableIndex === null) return
    setHeaderTypesByTable((prev) => ({ ...prev, [selectedTableIndex]: next }))
    if (!table) return
    const changed = reclassifyHeaders(table, next)
    if (!changed.length) return
    recordHistory()
    // One batched patch per resulting class. Level-I/II come from a cell IN the group, not
    // `changed[0]`: shrinking the band emits clears alongside sets, so one sampled cell
    // would stamp the wrong pair on the other group.
    for (const levelIII of new Set(changed.map((c) => c.pnid_level_iii))) {
      const group = changed.filter((c) => c.pnid_level_iii === levelIII)
      updateMultipleNodes(group.map((c) => c.id), {
        pnid_level_i: group[0].pnid_level_i,
        pnid_level_ii: group[0].pnid_level_ii,
        pnid_level_iii: levelIII,
      })
    }
    toast.success(t('graph_editor:tableGrid.headersRetyped', {
      count: changed.length,
      defaultValue: 'Re-typed {{count}} header cells',
    }))
  }, [table, recordHistory, updateMultipleNodes, t])

  if (!isOpen) return null

  const labelClass = 'text-xs font-semibold text-foreground'
  const selectClass = 'h-8 px-2 rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card text-sm text-foreground'

  const cellInputClass = (isHeader: boolean) =>
    `w-full min-w-[92px] h-8 px-2 text-sm bg-transparent text-foreground outline-none focus:bg-primary/5 focus:ring-1 focus:ring-primary rounded-sm ${
      isHeader ? 'font-semibold' : ''
    }`

  return (
    <div className="w-[560px] max-w-[60vw] h-full border-l border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card flex flex-col shrink-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 shrink-0 border-b border-[#e5e5e5] dark:border-dark-border">
        <h3 className="text-base font-semibold text-foreground">
          {t('graph_editor:tableGrid.title', { defaultValue: 'Tables' })}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={String(t('graph_editor:nodePanel.close', { defaultValue: 'Close' }))}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Table selector + New */}
      <div className="flex items-center gap-3 px-6 py-3 shrink-0 border-b border-[#e5e5e5] dark:border-dark-border">
        <label htmlFor="table-select" className={labelClass}>
          {t('graph_editor:tableGrid.table', { defaultValue: 'Table' })}
        </label>
        <select
          id="table-select"
          className={`${selectClass} flex-1`}
          value={selectedTableIndex ?? ''}
          disabled={tables.length === 0}
          onChange={(e) => {
            // Drop any half-typed cell: its id belongs to the table being left, so a later
            // blur would compare against a stale draft and silently discard the edit.
            setDraft(null)
            setSelectedTableIndex(Number(e.target.value))
          }}
        >
          {tables.length === 0 && (
            <option value="">{t('graph_editor:tableGrid.noTables', { defaultValue: 'No tables' })}</option>
          )}
          {tables.map((tb, i) => (
            <option key={tb.tableIndex} value={tb.tableIndex}>
              {t('graph_editor:tableGrid.tableOption', {
                number: i + 1,
                rows: tb.nRows,
                cols: tb.nCols,
                defaultValue: 'Table {{number}} — {{rows}}×{{cols}}',
              })}
            </option>
          ))}
        </select>
        {onCreateTable && (
          <Button
            size="sm"
            variant="outline"
            aria-pressed={isDrawingRegion}
            className={`gap-1 shrink-0 ${isDrawingRegion ? 'border-primary text-primary bg-primary/10' : ''}`}
            onClick={onCreateTable}
          >
            <Plus className="w-4 h-4" />
            {t('graph_editor:tableGrid.newTable', { defaultValue: 'New table' })}
          </Button>
        )}
      </div>

      {/* The only in-panel signal that the click took effect — the canvas crosshair is
          invisible until the pointer leaves the panel. */}
      {isDrawingRegion && !pendingRegion && (
        <div className="flex items-center gap-2 px-6 py-2.5 shrink-0 border-b border-primary/30 bg-primary/10 text-sm text-primary">
          <MousePointerSquareDashed className="w-4 h-4 shrink-0" />
          {t('graph_editor:tableGrid.drawRegionHint', {
            defaultValue: 'Draw a box around the table on the drawing.',
          })}
        </div>
      )}

      {/* Asked after the box, not before, so the rows can be counted off the drawing with
          the region already outlined on it. */}
      {/* Offered first when OCR found a table: it fills the values and boxes the cells
          correctly. The manual size form below is the fallback. */}
      {pendingRegion && detected && (
        <div className="flex items-center gap-3 px-6 py-3 shrink-0 border-b border-[#e5e5e5] dark:border-dark-border bg-primary/10">
          <div className="flex-1 text-sm text-foreground">
            {t('graph_editor:tableGrid.detectedSummary', {
              rows: detected.nRows, cols: detected.nCols, values: detected.filledCells,
              defaultValue: 'Found a {{rows}}×{{cols}} table with {{values}} values in this region.',
            })}
          </div>
          <Button size="sm" className="shrink-0" onClick={createTableFromText}>
            {t('graph_editor:tableGrid.useDetected', { defaultValue: 'Read from drawing' })}
          </Button>
        </div>
      )}

      {pendingRegion && (
        <div className="flex items-end gap-3 px-6 py-3 shrink-0 border-b border-[#e5e5e5] dark:border-dark-border bg-primary/5">
          <div>
            <label htmlFor="new-table-rows" className={`${labelClass} block mb-1`}>
              {t('graph_editor:tableGrid.rowsPrompt', { defaultValue: 'How many rows (including the header row)?' })}
            </label>
            <input
              id="new-table-rows"
              type="number"
              min={1}
              max={200}
              value={newRows}
              onChange={(e) => setNewRows(e.target.value)}
              className={`${selectClass} w-20`}
            />
          </div>
          <div>
            <label htmlFor="new-table-cols" className={`${labelClass} block mb-1`}>
              {t('graph_editor:tableGrid.colsPrompt', { defaultValue: 'How many columns (including the property column)?' })}
            </label>
            <input
              id="new-table-cols"
              type="number"
              min={1}
              max={100}
              value={newCols}
              onChange={(e) => setNewCols(e.target.value)}
              className={`${selectClass} w-20`}
            />
          </div>
          <Button size="sm" variant={detected ? 'outline' : 'default'} className="shrink-0" onClick={createBlankTable}>
            {t('graph_editor:tableGrid.blankTable', { defaultValue: 'Blank grid' })}
          </Button>
        </div>
      )}

      {/* Positional, not content-matched. The row count is a control rather than a constant
          because real tables stack header rows (names over numbers) and only the annotator
          can see how many a given drawing has. */}
      {table && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-6 py-3 shrink-0 border-b border-[#e5e5e5] dark:border-dark-border">
          <label htmlFor="header-row-count" className={labelClass}>
            {t('graph_editor:tableGrid.headerRows', { defaultValue: 'Header rows' })}
          </label>
          <select
            id="header-row-count"
            className={`${selectClass} w-16`}
            value={headerTypes.rowTypes.length}
            onChange={(e) => changeHeaderTypes(withHeaderRowCount(headerTypes, Number(e.target.value)))}
          >
            {Array.from({ length: MAX_HEADER_ROWS }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>

          {headerTypes.rowTypes.map((type, row) => (
            <span key={row} className="flex items-center gap-2">
              <label htmlFor={`header-row-type-${row}`} className={labelClass}>
                {t('graph_editor:tableGrid.rowNIs', {
                  number: row + 1,
                  defaultValue: 'Row {{number}} is',
                })}
              </label>
              <select
                id={`header-row-type-${row}`}
                className={selectClass}
                value={type}
                onChange={(e) => changeHeaderTypes({
                  ...headerTypes,
                  rowTypes: headerTypes.rowTypes.map((v, i) => (i === row ? e.target.value : v)),
                })}
              >
                {TOP_ROW_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </span>
          ))}

          <label htmlFor="left-col-type" className={labelClass}>
            {t('graph_editor:tableGrid.leftColumnIs', { defaultValue: 'Left column is' })}
          </label>
          <select
            id="left-col-type"
            className={selectClass}
            value={headerTypes.leftCol}
            onChange={(e) => changeHeaderTypes({ ...headerTypes, leftCol: e.target.value })}
          >
            {LEFT_COL_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      )}

      {/* Grid */}
      <div ref={gridRef} className="flex-1 overflow-auto px-6 py-4">
        {!table ? (
          <div className="h-full flex flex-col items-center justify-center text-center gap-3 text-muted-foreground">
            <TableIcon className="w-8 h-8" />
            <p className="text-sm max-w-[320px]">
              {t('graph_editor:tableGrid.emptyState', {
                defaultValue: 'No table was parsed on this drawing. Use "New table" to draw the table region and fill in the grid.',
              })}
            </p>
          </div>
        ) : (
          <table className="border-collapse">
            <tbody>
              {Array.from({ length: table.nRows }, (_, row) => (
                <tr key={row}>
                  {Array.from({ length: table.nCols }, (_, col) => {
                    // Swallowed by a merged cell to its left — render nothing at all, or the
                    // row would be one <td> too wide and every later column would shift.
                    if (coveredBySpan(table, row, col)) return null
                    const cell = table.cellByKey.get(cellKey(row, col))
                    // Shade every declared header row, so the banding matches what gets
                    // classified.
                    const isHeader = row < headerTypes.rowTypes.length || col === 0
                    return (
                      <td
                        key={col}
                        colSpan={cell?.col_span && cell.col_span > 1 ? cell.col_span : undefined}
                        className={`border border-[#e5e5e5] dark:border-dark-border p-0 ${
                          isHeader ? 'bg-[#f5f5f5] dark:bg-dark-muted' : ''
                        }`}
                      >
                        {cell ? (
                          <input
                            type="text"
                            data-cell-id={cell.id}
                            aria-label={t('graph_editor:tableGrid.cellLabel', {
                              row: row + 1,
                              col: col + 1,
                              defaultValue: 'Row {{row}} column {{col}}',
                            })}
                            className={cellInputClass(isHeader)}
                            value={draft?.id === cell.id ? draft.value : cellText(cell)}
                            onChange={(e) => setDraft({ id: cell.id, value: e.target.value })}
                            onFocus={() => onFocusCellChange?.(cell.id)}
                            onBlur={(e) => commitCell(cell, e.target.value)}
                          />
                        ) : (
                          // A coordinate the parse never produced — left as a visible hole,
                          // since the drawing may have a merged cell there.
                          <div className="w-full min-w-[92px] h-8" aria-hidden="true" />
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Row/column actions */}
      {table && (
        <div className="flex flex-wrap items-center gap-2 px-6 py-3 border-t border-[#e5e5e5] dark:border-dark-border shrink-0">
          <Button size="sm" variant="outline" className="gap-1" onClick={() => applyMutation(insertRow(table, table.nRows, headerTypes))}>
            <Plus className="w-4 h-4" />
            {t('graph_editor:tableGrid.addRow', { defaultValue: 'Add row' })}
          </Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => applyMutation(insertColumn(table, table.nCols, headerTypes))}>
            <Plus className="w-4 h-4" />
            {t('graph_editor:tableGrid.addColumn', { defaultValue: 'Add column' })}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
            disabled={table.nRows <= 1}
            onClick={() => applyMutation(deleteRow(table, table.nRows - 1))}
          >
            <Trash2 className="w-4 h-4" />
            {t('graph_editor:tableGrid.removeLastRow', { defaultValue: 'Remove last row' })}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
            disabled={table.nCols <= 1}
            onClick={() => applyMutation(deleteColumn(table, table.nCols - 1))}
          >
            <Trash2 className="w-4 h-4" />
            {t('graph_editor:tableGrid.removeLastColumn', { defaultValue: 'Remove last column' })}
          </Button>
          {/* Merge/unmerge appear only for a canvas selection they can act on: pick the
              Select tool and click the cells. Rendered conditionally rather than disabled —
              with five buttons already here, a permanently greyed pair is just noise. */}
          {canMerge && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              data-testid="table-merge-cells"
              title={t('graph_editor:tableGrid.mergeHint', {
                count: selection.length,
                defaultValue: 'Merge the {{count}} selected cells into one',
              })}
              onClick={() => {
                if (!table) return
                applyMutation(mergeCells(table, selectedCellIds))
              }}
            >
              <TableCellsMerge className="w-4 h-4" />
              {t('graph_editor:tableGrid.merge', {
                count: selection.length,
                defaultValue: 'Merge {{count}} cells',
              })}
            </Button>
          )}
          {canUnmerge && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              data-testid="table-unmerge"
              title={t('graph_editor:tableGrid.unmergeHint', {
                defaultValue: 'Split the merged cells back into one cell per column',
              })}
              onClick={() => {
                if (!table) return
                applyMutation(unmergeCells(table, selectedCellIds, headerTypes))
              }}
            >
              <TableCellsSplit className="w-4 h-4" />
              {t('graph_editor:tableGrid.unmerge', { defaultValue: 'Unmerge' })}
            </Button>
          )}
          {/* Row/column removal stops at 1x1, so this is the only way out of a table drawn
              by mistake. One Undo restores it, so no confirm. */}
          <Button
            size="sm"
            variant="outline"
            className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => {
              const count = table.cellByKey.size
              applyMutation(deleteTable(table))
              toast.success(t('graph_editor:tableGrid.tableDeleted', {
                count, defaultValue: 'Table removed ({{count}} cells)',
              }))
            }}
          >
            <Trash2 className="w-4 h-4" />
            {t('graph_editor:tableGrid.deleteTable', { defaultValue: 'Delete table' })}
          </Button>
        </div>
      )}
    </div>
  )
}
